import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import axios from 'axios';

// Decomposed Sales Admin Components
import { CustomerOnboardDialog } from '../components/sales-admin/CustomerOnboardDialog';
import { CustomerViewDialog } from '../components/sales-admin/CustomerViewDialog';
import { CustomerEditDialog } from '../components/sales-admin/CustomerEditDialog';
import { AddLicenseDialog } from '../components/sales-admin/AddLicenseDialog';
import { OEMUserDialog } from '../components/sales-admin/OEMUserDialog';

import {
  LayoutDashboard,
  Building2,
  Users,
  FileText,
  Settings,
  Sun,
  Moon,
  LogOut,
  Plus,
  Search,
  Filter,
  MoreVertical,
  Edit,
  Trash2,
  Eye,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Calendar,
  CreditCard,
  TrendingUp,
  Clock,
  Mail,
  Phone,
  MapPin,
  RefreshCw,
  Download,
  Send,
  Loader2,
  Shield,
  Crown,
  Sparkles,
  ChevronRight,
  ArrowLeft,
  KeyRound,
  Copy,
  Power,
  MailPlus,
  UserX,
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GYANODAYA_LOGO = 'https://static.prod-images.emergentagent.com/jobs/2d9de157-6aba-4791-b4d7-cfe9f78303cb/images/caa72923253f39d305789ea60b60a4f20b404a1e2c8d1c106a92bfb7211cd8c8.png';

// Edition colors
const EDITION_COLORS = {
  standard: 'bg-slate-500',
  professional: 'bg-blue-500',
  enterprise: 'bg-purple-500',
};

const EDITION_ICONS = {
  standard: Shield,
  professional: Sparkles,
  enterprise: Crown,
};

// ===================== SALES ADMIN LAYOUT =====================
const SalesAdminLayout = ({ children, onLogout, user }) => {
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = [
    { path: '/sales-admin', label: 'Dashboard', icon: LayoutDashboard, exact: true },
    { path: '/sales-admin/customers', label: 'Customers', icon: Building2 },
    { path: '/sales-admin/users', label: 'OEM Users', icon: Users },
    { path: '/sales-admin/reports', label: 'Reports', icon: FileText },
    { path: '/sales-admin/settings', label: 'Settings', icon: Settings },
  ];

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 z-40 hidden lg:flex h-screen w-64 flex-col border-r bg-card">
        {/* Logo */}
        <div className="p-4 border-b">
          <div className="flex items-center gap-3">
            <img src={GYANODAYA_LOGO} alt="Gyanodaya" className="h-10 w-10" />
            <div>
              <h1 className="text-lg font-bold">GYANODAYA</h1>
              <p className="text-xs text-muted-foreground">Sales Admin</p>
            </div>
          </div>
        </div>

        {/* User Info */}
        {user && (
          <div className="p-4 border-b bg-muted/30">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center text-white text-sm font-bold">
                {user.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user.role}</p>
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 p-2 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                isActive(item.path, item.exact)
                  ? 'bg-primary text-primary-foreground'
                  : 'hover:bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              <item.icon className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
            </Link>
          ))}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t space-y-2">
          <Button variant="ghost" className="w-full justify-start" onClick={toggleTheme}>
            {theme === 'dark' ? <Sun className="w-4 h-4 mr-2" /> : <Moon className="w-4 h-4 mr-2" />}
            {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
          </Button>
          <Button variant="ghost" className="w-full justify-start" onClick={() => navigate('/')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Main App
          </Button>
          {onLogout && (
            <Button variant="ghost" className="w-full justify-start text-destructive" onClick={onLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen">
        {children}
      </main>
    </div>
  );
};

// ===================== DASHBOARD =====================
const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/oem/reports/dashboard`);
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">OEM Dashboard</h1>
          <p className="text-muted-foreground">Manage your Gyanodaya SaaS customers</p>
        </div>
        <Button onClick={fetchStats} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Customers</p>
                <p className="text-3xl font-bold">{stats?.tenants?.total || 0}</p>
              </div>
              <Building2 className="w-10 h-10 text-primary/20" />
            </div>
            <div className="mt-2 flex gap-2 text-xs">
              <Badge variant="secondary" className="bg-green-500/10 text-green-600">
                {stats?.tenants?.active || 0} active
              </Badge>
              {stats?.tenants?.suspended > 0 && (
                <Badge variant="secondary" className="bg-red-500/10 text-red-600">
                  {stats?.tenants?.suspended} suspended
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Licenses</p>
                <p className="text-3xl font-bold">
                  {(stats?.licenses?.total_creators || 0) + (stats?.licenses?.total_viewers || 0)}
                </p>
              </div>
              <CreditCard className="w-10 h-10 text-primary/20" />
            </div>
            <div className="mt-2 text-xs text-muted-foreground">
              {stats?.licenses?.total_creators || 0} creators, {stats?.licenses?.total_viewers || 0} viewers
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">By Edition</p>
                <div className="flex gap-2 mt-2">
                  <Badge className="bg-slate-500">{stats?.by_edition?.standard || 0}</Badge>
                  <Badge className="bg-blue-500">{stats?.by_edition?.professional || 0}</Badge>
                  <Badge className="bg-purple-500">{stats?.by_edition?.enterprise || 0}</Badge>
                </div>
              </div>
              <TrendingUp className="w-10 h-10 text-primary/20" />
            </div>
            <div className="mt-2 text-xs text-muted-foreground">
              Std / Pro / Ent
            </div>
          </CardContent>
        </Card>

        <Card className={stats?.alerts?.expiring_soon > 0 ? 'border-amber-500' : ''}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Renewals Due</p>
                <p className="text-3xl font-bold text-amber-500">{stats?.alerts?.expiring_soon || 0}</p>
              </div>
              <AlertTriangle className={`w-10 h-10 ${stats?.alerts?.expiring_soon > 0 ? 'text-amber-500' : 'text-primary/20'}`} />
            </div>
            <div className="mt-2 text-xs text-muted-foreground">
              Within next 30 days
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => window.location.href = '/sales-admin/customers?action=new'}>
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 rounded-lg bg-primary/10">
              <Plus className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold">Onboard Customer</h3>
              <p className="text-sm text-muted-foreground">Add new customer</p>
            </div>
            <ChevronRight className="w-5 h-5 ml-auto text-muted-foreground" />
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => window.location.href = '/sales-admin/reports?tab=renewal'}>
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 rounded-lg bg-amber-500/10">
              <Clock className="w-6 h-6 text-amber-500" />
            </div>
            <div>
              <h3 className="font-semibold">Renewal Report</h3>
              <p className="text-sm text-muted-foreground">View due renewals</p>
            </div>
            <ChevronRight className="w-5 h-5 ml-auto text-muted-foreground" />
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => window.location.href = '/sales-admin/reports?tab=activation'}>
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 rounded-lg bg-green-500/10">
              <FileText className="w-6 h-6 text-green-500" />
            </div>
            <div>
              <h3 className="font-semibold">Activation Report</h3>
              <p className="text-sm text-muted-foreground">License activations</p>
            </div>
            <ChevronRight className="w-5 h-5 ml-auto text-muted-foreground" />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ===================== CUSTOMERS =====================
const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterEdition, setFilterEdition] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewCustomer, setViewCustomer] = useState(null);
  const [addLicenseDialog, setAddLicenseDialog] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCustomers();
  }, [filterEdition, filterStatus]);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterEdition !== 'all') params.append('edition', filterEdition);
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (search) params.append('search', search);
      
      const response = await axios.get(`${API_URL}/oem/tenants?${params}`);
      setCustomers(response.data.tenants || []);
    } catch (error) {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchCustomers();
  };

  const openAddLicenseDialog = (customer) => {
    setSelectedCustomer(customer);
    setAddLicenseDialog(true);
  };

  // Handler for marking customer inactive/active
  const handleToggleStatus = async (customer, action) => {
    const actionLabel = action === 'inactive' ? 'deactivate' : 'reactivate';
    if (!window.confirm(`Are you sure you want to ${actionLabel} "${customer.company_name}"?`)) {
      return;
    }
    
    try {
      await axios.put(`${API_URL}/oem/tenants/${customer.id}/status?action=${action}`);
      toast.success(`Customer ${action === 'inactive' ? 'deactivated' : 'reactivated'} successfully`);
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${actionLabel} customer`);
    }
  };

  // Handler for deleting customer
  const handleDeleteCustomer = async (customer) => {
    if (!window.confirm(`⚠️ WARNING: This will permanently delete "${customer.company_name}" and ALL associated data (users, courses, enrollments).\n\nThis action cannot be undone!\n\nAre you sure you want to proceed?`)) {
      return;
    }
    
    // Double confirmation for delete
    const confirmText = window.prompt(`To confirm deletion, type the site ID: "${customer.site_id}"`);
    if (confirmText !== customer.site_id) {
      toast.error('Deletion cancelled - site ID did not match');
      return;
    }
    
    try {
      const response = await axios.put(`${API_URL}/oem/tenants/${customer.id}/status?action=delete`);
      toast.success(response.data.message);
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete customer');
    }
  };

  // Handler for resending welcome email
  const handleResendWelcome = async (customer) => {
    if (!window.confirm(`Resend welcome email to ${customer.contact_person?.email}?`)) {
      return;
    }
    
    try {
      const response = await axios.post(`${API_URL}/oem/tenants/${customer.id}/resend-welcome`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend welcome email');
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Customers</h1>
          <p className="text-muted-foreground">Manage your Gyanodaya customers</p>
        </div>
        <Button onClick={() => setDialogOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Onboard Customer
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by company, site ID, email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={filterEdition} onValueChange={setFilterEdition}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Edition" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Editions</SelectItem>
                <SelectItem value="standard">Standard</SelectItem>
                <SelectItem value="professional">Professional</SelectItem>
                <SelectItem value="enterprise">Enterprise</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={handleSearch}>
              <Filter className="w-4 h-4 mr-2" />
              Apply
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Customers Table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Company</TableHead>
              <TableHead>Site ID</TableHead>
              <TableHead>License Key</TableHead>
              <TableHead>Edition</TableHead>
              <TableHead>Licenses</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array(5).fill(0).map((_, i) => (
                <TableRow key={i}>
                  <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-8" /></TableCell>
                </TableRow>
              ))
            ) : customers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  No customers found. Click "Onboard Customer" to add your first customer.
                </TableCell>
              </TableRow>
            ) : (
              customers.map((customer) => {
                const EditionIcon = EDITION_ICONS[customer.edition] || Shield;
                const daysRemaining = customer.license?.days_remaining;
                const isExpiringSoon = daysRemaining !== undefined && daysRemaining <= 30 && daysRemaining > 0;
                const isExpired = daysRemaining !== undefined && daysRemaining <= 0;
                const isActivated = customer.license?.is_activated;
                
                return (
                  <TableRow key={customer.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{customer.company_name}</p>
                        <p className="text-xs text-muted-foreground">{customer.contact_person?.email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <code className="text-sm bg-muted px-2 py-1 rounded">{customer.site_id}</code>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <code className="text-xs font-mono">{customer.license?.license_key || 'N/A'}</code>
                        {isActivated ? (
                          <Badge variant="default" className="text-xs px-1 py-0">✓</Badge>
                        ) : (
                          <Badge variant="secondary" className="text-xs px-1 py-0">Pending</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${EDITION_COLORS[customer.edition]} text-white`}>
                        <EditionIcon className="w-3 h-3 mr-1" />
                        {customer.edition}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <span className="text-green-600">{customer.license?.creator_licenses || 0}C</span>
                        {' / '}
                        <span className="text-blue-600">{customer.license?.viewer_licenses || 0}V</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Badge variant={customer.status === 'active' ? 'default' : customer.status === 'inactive' ? 'secondary' : 'destructive'}>
                          {customer.status === 'active' ? <CheckCircle className="w-3 h-3 mr-1" /> : <XCircle className="w-3 h-3 mr-1" />}
                          {customer.status}
                        </Badge>
                        {isExpiringSoon && <Badge variant="outline" className="text-amber-500 border-amber-500 text-xs">{daysRemaining}d</Badge>}
                        {isExpired && <Badge variant="outline" className="text-red-500 border-red-500 text-xs">Expired</Badge>}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => setViewCustomer(customer)} data-testid={`view-customer-${customer.site_id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => { setEditingCustomer(customer); setEditDialogOpen(true); }} data-testid={`edit-customer-${customer.site_id}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openAddLicenseDialog(customer)} data-testid={`add-license-${customer.site_id}`}>
                          <Plus className="w-4 h-4" />
                        </Button>
                        
                        {/* More Actions Dropdown */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`more-actions-${customer.site_id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {/* Resend Welcome Email - Only for active, non-activated customers */}
                            {customer.status === 'active' && !isActivated && customer.license?.license_key && (
                              <>
                                <DropdownMenuItem 
                                  onClick={() => handleResendWelcome(customer)}
                                  data-testid={`resend-welcome-${customer.site_id}`}
                                >
                                  <MailPlus className="w-4 h-4 mr-2" />
                                  Resend Welcome Email
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                              </>
                            )}
                            
                            {/* Toggle Active/Inactive Status */}
                            {customer.status === 'active' ? (
                              <DropdownMenuItem 
                                onClick={() => handleToggleStatus(customer, 'inactive')}
                                className="text-amber-600"
                                data-testid={`deactivate-${customer.site_id}`}
                              >
                                <Power className="w-4 h-4 mr-2" />
                                Mark Inactive
                              </DropdownMenuItem>
                            ) : customer.status === 'inactive' && (
                              <DropdownMenuItem 
                                onClick={() => handleToggleStatus(customer, 'active')}
                                className="text-green-600"
                                data-testid={`reactivate-${customer.site_id}`}
                              >
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Reactivate
                              </DropdownMenuItem>
                            )}
                            
                            <DropdownMenuSeparator />
                            
                            {/* Delete Customer */}
                            <DropdownMenuItem 
                              onClick={() => handleDeleteCustomer(customer)}
                              className="text-red-600"
                              data-testid={`delete-customer-${customer.site_id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete Customer
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Onboard Customer Dialog */}
      <CustomerOnboardDialog 
        open={dialogOpen} 
        onOpenChange={setDialogOpen}
        onSuccess={() => {
          setDialogOpen(false);
          fetchCustomers();
        }}
      />

      {/* View Customer Dialog */}
      <CustomerViewDialog
        customer={viewCustomer}
        open={!!viewCustomer}
        onOpenChange={(open) => !open && setViewCustomer(null)}
      />

      {/* Edit Customer Dialog */}
      <CustomerEditDialog
        customer={editingCustomer}
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        onSuccess={() => {
          setEditDialogOpen(false);
          setEditingCustomer(null);
          fetchCustomers();
        }}
      />

      {/* Add License Dialog */}
      <AddLicenseDialog
        customer={selectedCustomer}
        open={addLicenseDialog}
        onOpenChange={setAddLicenseDialog}
        onSuccess={() => {
          setAddLicenseDialog(false);
          setSelectedCustomer(null);
          fetchCustomers();
        }}
      />
    </div>
  );
};

// ===================== REPORTS =====================
const Reports = () => {
  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold">Reports</h1>
      
      <Tabs defaultValue="activation">
        <TabsList>
          <TabsTrigger value="activation">License Activation</TabsTrigger>
          <TabsTrigger value="renewal">Renewal Due</TabsTrigger>
        </TabsList>
        
        <TabsContent value="activation">
          <ActivationReport />
        </TabsContent>
        
        <TabsContent value="renewal">
          <RenewalReport />
        </TabsContent>
      </Tabs>
    </div>
  );
};

const ActivationReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReport();
  }, []);

  const fetchReport = async () => {
    try {
      const response = await axios.get(`${API_URL}/oem/reports/license-activation`);
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Skeleton className="h-64" />;

  return (
    <Card>
      <CardHeader>
        <CardTitle>License Activations</CardTitle>
        <CardDescription>All activated licenses</CardDescription>
      </CardHeader>
      <CardContent>
        {/* Summary */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold">{data?.summary?.standard?.count || 0}</p>
              <p className="text-sm text-muted-foreground">Standard</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-blue-500">{data?.summary?.professional?.count || 0}</p>
              <p className="text-sm text-muted-foreground">Professional</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-purple-500">{data?.summary?.enterprise?.count || 0}</p>
              <p className="text-sm text-muted-foreground">Enterprise</p>
            </CardContent>
          </Card>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Company</TableHead>
              <TableHead>Edition</TableHead>
              <TableHead>Licenses</TableHead>
              <TableHead>Activated</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data?.activations?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                  No activations yet
                </TableCell>
              </TableRow>
            ) : (
              data?.activations?.map((tenant) => (
                <TableRow key={tenant.id}>
                  <TableCell>{tenant.company_name}</TableCell>
                  <TableCell>
                    <Badge className={EDITION_COLORS[tenant.edition]}>{tenant.edition}</Badge>
                  </TableCell>
                  <TableCell>
                    {tenant.license?.creator_licenses}C / {tenant.license?.viewer_licenses}V
                  </TableCell>
                  <TableCell>{new Date(tenant.created_at).toLocaleDateString()}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

const RenewalReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchReport();
  }, []);

  const fetchReport = async () => {
    try {
      const response = await axios.get(`${API_URL}/oem/reports/renewal-due?days_ahead=30`);
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const sendReminders = async () => {
    setSending(true);
    try {
      const response = await axios.post(`${API_URL}/oem/send-renewal-reminders`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Failed to send reminders');
    } finally {
      setSending(false);
    }
  };

  if (loading) return <Skeleton className="h-64" />;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Renewal Due</CardTitle>
            <CardDescription>Licenses expiring in next 30 days</CardDescription>
          </div>
          <Button onClick={sendReminders} disabled={sending}>
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Send Reminders
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Summary */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <Card className="border-amber-500">
            <CardContent className="p-4 text-center">
              <AlertTriangle className="w-8 h-8 mx-auto text-amber-500 mb-2" />
              <p className="text-2xl font-bold text-amber-500">{data?.summary?.expiring_count || 0}</p>
              <p className="text-sm text-muted-foreground">Expiring Soon</p>
            </CardContent>
          </Card>
          <Card className="border-red-500">
            <CardContent className="p-4 text-center">
              <XCircle className="w-8 h-8 mx-auto text-red-500 mb-2" />
              <p className="text-2xl font-bold text-red-500">{data?.summary?.expired_count || 0}</p>
              <p className="text-sm text-muted-foreground">Already Expired</p>
            </CardContent>
          </Card>
        </div>

        {/* Expiring Soon */}
        {data?.expiring_soon?.length > 0 && (
          <>
            <h4 className="font-medium mb-2 text-amber-500">Expiring Soon</h4>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead>Days Left</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead>Contact</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.expiring_soon.map((tenant) => (
                  <TableRow key={tenant.id}>
                    <TableCell>{tenant.company_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-amber-500 border-amber-500">
                        {tenant.license?.days_remaining} days
                      </Badge>
                    </TableCell>
                    <TableCell>{new Date(tenant.license?.end_date).toLocaleDateString()}</TableCell>
                    <TableCell>{tenant.contact_person?.email}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}

        {/* Already Expired */}
        {data?.already_expired?.length > 0 && (
          <>
            <h4 className="font-medium mb-2 mt-6 text-red-500">Already Expired</h4>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead>Expired</TableHead>
                  <TableHead>Contact</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.already_expired.map((tenant) => (
                  <TableRow key={tenant.id}>
                    <TableCell>{tenant.company_name}</TableCell>
                    <TableCell>{new Date(tenant.license?.end_date).toLocaleDateString()}</TableCell>
                    <TableCell>{tenant.contact_person?.email}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}

        {data?.expiring_soon?.length === 0 && data?.already_expired?.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500" />
            <p>All licenses are up to date!</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ===================== OEM USERS =====================
const OEMUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/oem/users`);
      setUsers(response.data.users || []);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">OEM Users</h1>
          <p className="text-muted-foreground">Manage platform operators and salespeople</p>
        </div>
        <Button onClick={() => setDialogOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Add User
        </Button>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array(3).fill(0).map((_, i) => (
                <TableRow key={i}>
                  <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                </TableRow>
              ))
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  No OEM users yet
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{user.role}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={user.is_active ? 'default' : 'secondary'}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>{new Date(user.created_at).toLocaleDateString()}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Add User Dialog */}
      <OEMUserDialog 
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSuccess={() => {
          setDialogOpen(false);
          fetchUsers();
        }}
      />
    </div>
  );
};


// ===================== SETTINGS =====================
const OEMSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [smtpConfig, setSmtpConfig] = useState({
    host: '',
    port: 587,
    username: '',
    password: '',
    from_email: '',
    from_name: 'Gyanodaya',
    use_tls: true,
    configured: false,
  });

  useEffect(() => {
    fetchSMTPSettings();
  }, []);

  const fetchSMTPSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/oem/settings/smtp`);
      if (response.data.configured) {
        setSmtpConfig({ ...smtpConfig, ...response.data });
      }
    } catch (error) {
      console.log('No SMTP settings found');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!smtpConfig.host || !smtpConfig.username || !smtpConfig.password || !smtpConfig.from_email) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API_URL}/oem/settings/smtp`, {
        ...smtpConfig,
        configured: true,
      });
      toast.success('SMTP settings saved successfully');
      setSmtpConfig({ ...smtpConfig, configured: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save SMTP settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testEmail) {
      toast.error('Please enter a test email address');
      return;
    }

    setTesting(true);
    try {
      const response = await axios.post(`${API_URL}/oem/settings/smtp/test?test_email=${encodeURIComponent(testEmail)}`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'SMTP test failed');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Settings</h1>
        <p className="text-muted-foreground">Configure SMTP and notification settings</p>
      </div>

      <Tabs defaultValue="smtp">
        <TabsList>
          <TabsTrigger value="smtp">
            <Mail className="w-4 h-4 mr-2" />
            SMTP Configuration
          </TabsTrigger>
        </TabsList>

        <TabsContent value="smtp" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Email Server Settings
              </CardTitle>
              <CardDescription>
                Configure your SMTP server to send welcome emails, license notifications, and renewal reminders.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {smtpConfig.configured && (
                <div className="flex items-center gap-2 p-3 bg-green-500/10 text-green-600 rounded-lg">
                  <CheckCircle className="w-5 h-5" />
                  <span>SMTP is configured and ready to send emails</span>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>SMTP Host *</Label>
                  <Input
                    value={smtpConfig.host}
                    onChange={(e) => setSmtpConfig({ ...smtpConfig, host: e.target.value })}
                    placeholder="smtp.gmail.com"
                    data-testid="smtp-host"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Port *</Label>
                  <Input
                    type="number"
                    value={smtpConfig.port}
                    onChange={(e) => setSmtpConfig({ ...smtpConfig, port: parseInt(e.target.value) || 587 })}
                    placeholder="587"
                    data-testid="smtp-port"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Username *</Label>
                  <Input
                    value={smtpConfig.username}
                    onChange={(e) => setSmtpConfig({ ...smtpConfig, username: e.target.value })}
                    placeholder="your-email@domain.com"
                    data-testid="smtp-username"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Password *</Label>
                  <Input
                    type="password"
                    value={smtpConfig.password}
                    onChange={(e) => setSmtpConfig({ ...smtpConfig, password: e.target.value })}
                    placeholder={smtpConfig.configured ? '********' : 'Enter password'}
                    data-testid="smtp-password"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>From Email *</Label>
                  <Input
                    type="email"
                    value={smtpConfig.from_email}
                    onChange={(e) => setSmtpConfig({ ...smtpConfig, from_email: e.target.value })}
                    placeholder="noreply@gyanodaya.com"
                    data-testid="smtp-from-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label>From Name</Label>
                  <Input
                    value={smtpConfig.from_name}
                    onChange={(e) => setSmtpConfig({ ...smtpConfig, from_name: e.target.value })}
                    placeholder="Gyanodaya"
                    data-testid="smtp-from-name"
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="use-tls"
                  checked={smtpConfig.use_tls}
                  onChange={(e) => setSmtpConfig({ ...smtpConfig, use_tls: e.target.checked })}
                  className="h-4 w-4"
                />
                <Label htmlFor="use-tls">Use TLS (recommended for port 587)</Label>
              </div>

              <div className="flex gap-2">
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                  Save Settings
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Test Email */}
          <Card>
            <CardHeader>
              <CardTitle>Test SMTP Connection</CardTitle>
              <CardDescription>Send a test email to verify your SMTP configuration is working.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-4">
                <Input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="Enter test email address"
                  className="flex-1"
                  data-testid="smtp-test-email"
                />
                <Button onClick={handleTest} disabled={testing || !smtpConfig.configured} variant="outline">
                  {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Send Test
                </Button>
              </div>
              {!smtpConfig.configured && (
                <p className="text-sm text-muted-foreground">Save SMTP settings first before testing.</p>
              )}
            </CardContent>
          </Card>

          {/* Email Types Info */}
          <Card>
            <CardHeader>
              <CardTitle>Email Notifications</CardTitle>
              <CardDescription>The following emails will be sent automatically:</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium flex items-center gap-2">
                    <Mail className="w-4 h-4 text-blue-500" />
                    Welcome Email
                  </h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    Sent when a new customer is onboarded. Contains license key and activation instructions.
                  </p>
                </div>
                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-green-500" />
                    License Addition
                  </h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    Sent when additional licenses are added to a customer's account.
                  </p>
                </div>
                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                    Renewal Reminder
                  </h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    Sent to customers when their license is about to expire (30, 14, 7, 1 days before).
                  </p>
                </div>
                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium flex items-center gap-2">
                    <Users className="w-4 h-4 text-purple-500" />
                    Salesperson Alert
                  </h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    Sent to assigned salesperson when their customer's license is expiring.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

// ===================== MAIN SALES ADMIN =====================
export const SalesAdmin = () => {
  const [oemUser, setOemUser] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('oem_token');
    const user = localStorage.getItem('oem_user');
    
    if (token && user) {
      try {
        const parsedUser = JSON.parse(user);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        setOemUser(parsedUser);
      } catch (e) {
        localStorage.removeItem('oem_token');
        localStorage.removeItem('oem_user');
      }
    }
    setCheckingAuth(false);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('oem_token');
    localStorage.removeItem('oem_user');
    delete axios.defaults.headers.common['Authorization'];
    setOemUser(null);
    toast.success('Logged out successfully');
    navigate('/login?mode=oem');
  };

  if (checkingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  // Redirect to login page with OEM mode if not authenticated
  if (!oemUser) {
    navigate('/login?mode=oem');
    return null;
  }

  return (
    <SalesAdminLayout onLogout={handleLogout} user={oemUser}>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/customers" element={<Customers />} />
        <Route path="/users" element={<OEMUsers />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/settings" element={<OEMSettings />} />
      </Routes>
    </SalesAdminLayout>
  );
};

export default SalesAdmin;
